import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  RemoveCircleOutline as UnshareIcon
} from '@mui/icons-material';
import familyService from '../services/familyService';
import { useAppNavigation } from '../hooks/useAppNavigation';

const SharedBoxes = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sharedBoxes, setSharedBoxes] = useState([]);
  const [ownedBoxes, setOwnedBoxes] = useState([]);
  const navigate = useAppNavigation();

  useEffect(() => {
    loadSharedBoxes();
  }, []);

  const loadSharedBoxes = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('[SharedBoxes] Loading shared boxes...');
      const result = await familyService.getSharedBoxes();
      
      console.log('[SharedBoxes] API result:', result);
      
      if (result.success) {
        const sharedBoxesList = result.shared_boxes || [];
        const ownedBoxesList = result.owned_boxes || [];
        console.log('[SharedBoxes] Shared boxes:', sharedBoxesList.length);
        console.log('[SharedBoxes] Owned boxes:', ownedBoxesList.length);
        setSharedBoxes(sharedBoxesList);
        setOwnedBoxes(ownedBoxesList);
      } else {
        setError(result.error || 'Failed to load shared boxes');
      }
    } catch (err) {
      console.error('[SharedBoxes] Error loading:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnshareBox = async (boxId) => {
    if (!window.confirm('Are you sure you want to stop sharing this box with your family?')) {
      return;
    }

    try {
      setError('');
      await familyService.unshareBox(boxId);
      loadSharedBoxes(); // Reload the list
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewBox = (boxId) => {
    navigate.to.BOX_DETAIL(boxId);
  };

  const renderBoxCard = (box, isOwned) => (
    <Grid item xs={12} sm={6} md={4} key={box.id}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" component="div">
              {box.name}
            </Typography>
            <Chip
              size="small"
              label={box.permission_level === 'view' ? 'View Only' : 'Can Edit'}
              color={box.permission_level === 'view' ? 'default' : 'primary'}
              icon={box.permission_level === 'view' ? <ViewIcon /> : <EditIcon />}
            />
          </Box>
          
          {box.description && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              {box.description}
            </Typography>
          )}
          
          {box.location && (
            <Typography variant="body2" color="text.secondary">
              📍 {box.location}
            </Typography>
          )}
          
          {isOwned && (
            <Box mt={1}>
              <Chip
                size="small"
                label="Your Box"
                color="success"
                variant="outlined"
              />
            </Box>
          )}
        </CardContent>
        
        <CardActions>
          <Button
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => handleViewBox(box.id)}
          >
            View
          </Button>
          
          {isOwned && (
            <Button
              size="small"
              color="warning"
              startIcon={<UnshareIcon />}
              onClick={() => handleUnshareBox(box.id)}
            >
              Unshare
            </Button>
          )}
        </CardActions>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Boxes shared by family members */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" mb={2}>
          <ShareIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Shared With You ({sharedBoxes.length})
          </Typography>
        </Box>
        
        {sharedBoxes.length === 0 ? (
          <Alert severity="info">
            No boxes have been shared with you yet. Family members can share their boxes with you.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {sharedBoxes.map(box => renderBoxCard(box, false))}
          </Grid>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* User's boxes that are shared */}
      <Box>
        <Box display="flex" alignItems="center" mb={2}>
          <ShareIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Your Shared Boxes ({ownedBoxes.length})
          </Typography>
        </Box>
        
        {ownedBoxes.length === 0 ? (
          <Alert severity="info">
            You haven't shared any boxes with your family yet. Share boxes from the box detail page.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {ownedBoxes.map(box => renderBoxCard(box, true))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default SharedBoxes;
